import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import { piiMaskingService } from '@/core/infrastructure/services/PiiMaskingService.ts'

/**
 * Initialise Sentry pour le monitoring d'erreurs et de performances
 * @param app L'instance Vue
 * @param router Le routeur Vue
 */
export const initSentry = (app: App, router: Router): void => {
  if (!__SENTRY_DSN__) {
    console.warn('Sentry DSN not provided, monitoring disabled')
    return
  }

  Sentry.init({
    app,
    dsn: __SENTRY_DSN__,
    environment: __ENV_NAME__,
    release: `${__APP_NAME__}@${__APP_VERSION__}`,
    sendDefaultPii: false,

    // Intégrations avec le router
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        routeLabel: 'name'
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: false,
        mask: ['[data-maskable]'],
        unmask: ['[data-unmaskable]'],
        block: ['[data-blockable]'],
        ignore: ['[data-ignore-input]'],
        networkDetailAllowUrls: [globalThis.location.origin]
      })
    ],

    // Domaines à tracer
    tracePropagationTargets: ['localhost', __BASE_URL_API__, /https:\/\/.*\.genially\.(ly|com)\/.*/],

    // Taux d'échantillonnage
    tracesSampleRate: __SENTRY_TRACE_SAMPLE_RATE__ ?? 0.1,
    replaysSessionSampleRate: __SENTRY_REPLAY_SESSION_SAMPLE_RATE__ ?? 0.1,
    replaysOnErrorSampleRate: __SENTRY_REPLAY_ERROR_SAMPLE_RATE__ ?? 1,

    // Filtrage et masquage des erreurs
    beforeSend(event) {
      // Ne pas envoyer les erreurs d'authentification 401
      if (event.exception?.values?.some((ex) => ex.value?.includes('API_401_UNAUTHORIZED'))) {
        return null
      }

      // Retire infos user & entêtes sensibles si présents
      if (event.user) {
        delete event.user.email
        delete event.user.username
        delete event.user.name
        delete event.user.ip_address
      }
      if (event.request?.headers) {
        delete event.request.headers['authorization']
        delete event.request.headers['cookie']
      }

      // Masquer les PII dans les exceptions
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((exception) => ({
          ...exception,
          value: exception.value ? piiMaskingService.maskMessage(exception.value) : exception.value,
          // Masquer aussi les stack traces
          stacktrace: exception.stacktrace
            ? {
                ...exception.stacktrace,
                frames: exception.stacktrace.frames?.map((frame) => ({
                  ...frame,
                  // Masquer les variables locales qui peuvent contenir des PII
                  vars: frame.vars ? piiMaskingService.maskObject(frame.vars) : frame.vars
                }))
              }
            : exception.stacktrace
        }))
      }

      // Masquer les PII dans les extras
      if (event.extra) {
        event.extra = piiMaskingService.maskObject(event.extra)
      }

      // Masquer les PII dans les contextes
      if (event.contexts) {
        event.contexts = piiMaskingService.maskObject(event.contexts)
      }

      // Masquer les PII dans les breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          message: breadcrumb.message
            ? piiMaskingService.maskMessage(breadcrumb.message)
            : breadcrumb.message,
          data: breadcrumb.data ? piiMaskingService.maskObject(breadcrumb.data) : breadcrumb.data
        }))
      }

      // Masquer les PII dans les requêtes HTTP
      if (event.request) {
        // Masquer l'URL (peut contenir des tokens/emails)
        if (event.request.url) {
          event.request.url = piiMaskingService.maskMessage(event.request.url)
        }
        // Masquer les query params
        if (event.request.query_string) {
          event.request.query_string = piiMaskingService.maskMessage(event.request.query_string)
        }
        // Masquer les données POST
        if (event.request.data) {
          event.request.data = piiMaskingService.maskObject(event.request.data)
        }
      }

      return event
    },

    // Filtrage des transactions (performances)
    beforeSendTransaction(event) {
      // Filtrer les transactions trop longues (>30s = utilisateur inactif)
      // Évite de polluer Sentry avec des métriques non pertinentes
      if (event.timestamp && event.start_timestamp) {
        const duration = event.timestamp - event.start_timestamp
        if (duration > 30) {
          return null // Ne pas envoyer
        }
      }
      return event
    }
  })

  // Ajouter un handler global pour capturer les erreurs non gérées
  app.config.errorHandler = (error, instance, info) => {
    if (error instanceof NabooError) {
      // Ne pas dupliquer les rapports d'erreur déjà envoyés à Sentry
      if (!error.isSentryReported) {
        Sentry.captureException(error, {
          tags: {
            errorCode: error.code,
            errorStatus: error.status.toString()
          },
          extra: {
            componentInfo: info,
            details: error.details
          }
        })
        // Marquer comme reporté via la méthode captureToSentry
        error.captureToSentry()
      }
    } else {
      Sentry.captureException(error, {
        extra: {
          componentInfo: info,
          componentName: instance?.$options?.name
        }
      })
    }

    // Logguer en développement local
    if (__ENV_NAME__ === 'local') {
      console.error('Vue Error Handler:', error)
    }
  }
}
